import React, { FC } from 'react';
import { ElitepaymentPlan } from 'logic/api-models/api-models';
import styled from 'styled-components';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { InjectedFormProps, reduxForm } from 'redux-form';
import {
  MY_PRICING_FORM_NAME,
  MyPricingFormFields,
  validateMyPricingForm,
} from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import { PricePackages } from 'src/pages-all/user/user-profile/my-pricing/components/PricePackages';
import { FormSelection } from 'src/pages-all/user/user-profile/my-pricing/components/FormSelection';
import { Card } from 'antd';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { CHECKMARK_BADGE, LIGHTBULB_ICON } from 'assets/icons/icons';
import { Text } from 'components/text/Text';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { QUOTATIONS_GRIT_HOFFMANN } from 'assets/images/images';
import {
  InfoDescription,
  StyledInfoCard,
} from 'src/pages-all/user/user-profile/my-pricing/styles/my-pricing-styles';
import { Heading } from 'components/heading/Heading';
import { AppRoute } from 'app/app.route.const';
import { colors } from 'src/styles/colors';

const FormSelectionContainer = styled.div`
  display: flex;
`;

const InfoBoxContainer = styled.div`
  margin: 10px 0 0 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const InfoBoxItem = styled.div`
  margin-top: 20px;
`;

const QuotationBox = styled.div`
  display: grid;
  place-items: center;
`;

const MyPricing: FC<InjectedFormProps> = (props) => {
  const { maxDesktop } = useBreakpoints();

  const handlePricePackagesChanges = (lessonUnitCount: number) => {
    props.change(MyPricingFormFields.lessonUnitCount, lessonUnitCount);
  };

  const handleFormSelectionPlanChanges = (planCode: ElitepaymentPlan) => {
    props.change(MyPricingFormFields.plan, planCode);
  };

  return (
    <>
      <StyledInfoCard>
        <Heading size={22} align={'center'}>
          <CustomIcon loading="lazy" src={CHECKMARK_BADGE} width={40} />{' '}
          {translate(translationKeys.pricing.infoBox.headline)}
        </Heading>
        <InfoDescription>
          <Text color={'primary'}>{translate(translationKeys.pricing.infoBox.description1)}</Text>{' '}
          <Text>
            {translate(translationKeys.pricing.infoBox.description2.prefix)}{' '}
            <a
              href={AppRoute.FooterTOS}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary }}
            >
              {translate(translationKeys.pricing.infoBox.description2.agbLink)}
            </a>{' '}
            {translate(translationKeys.pricing.infoBox.description2.suffix)}
          </Text>
        </InfoDescription>
      </StyledInfoCard>
      <PricePackages listener={handlePricePackagesChanges} />
      <FormSelectionContainer>
        <FormSelection planlistener={handleFormSelectionPlanChanges} />
        {!maxDesktop && (
          <InfoBoxContainer>
            <Card style={{ height: 'fit-content' }}>
              <CustomIcon src={LIGHTBULB_ICON} height={28} />
              <InfoBoxItem>
                <Text size={16}>
                  {translate(translationKeys.pricing.selection.promotionBox.headline)}
                </Text>
              </InfoBoxItem>
              <InfoBoxItem>
                <Text size={16}>
                  {translate(translationKeys.pricing.selection.promotionBox.text1)}
                </Text>
              </InfoBoxItem>
              <InfoBoxItem>
                <Text size={16}>
                  {translate(translationKeys.pricing.selection.promotionBox.text2)}
                </Text>
              </InfoBoxItem>
            </Card>
            <Card style={{ flex: 1, marginTop: '10px' }}>
              <QuotationBox>
                <CustomIcon
                  src={QUOTATIONS_GRIT_HOFFMANN}
                  height={60}
                  style={{ borderRadius: '30px' }}
                />
                <Text
                  size={12}
                  fontWeight={400}
                  color={'blueGray'}
                  textAlign={'center'}
                  style={{ display: 'block', marginTop: '10px' }}
                >
                  {translate(translationKeys.pricing.selection.quotation.quote)}
                </Text>
                <Text
                  size={10}
                  fontWeight={600}
                  color={'blueGray'}
                  textAlign={'center'}
                  style={{ display: 'block', marginTop: '10px' }}
                >
                  {translate(translationKeys.pricing.selection.quotation.user)}
                </Text>
              </QuotationBox>
            </Card>
          </InfoBoxContainer>
        )}
      </FormSelectionContainer>
    </>
  );
};

export const MyPricingForm = reduxForm({
  form: MY_PRICING_FORM_NAME,
  validate: validateMyPricingForm,
  touchOnChange: true,
})(MyPricing);
